import type { ApiClientError } from '@contract-analyzer/api-client';
import { z } from 'zod';

type AuthRequiredEvent = {
  readonly error: ApiClientError;
};

type AuthLifecycleEvent = {
  readonly source: 'local' | 'remote';
};

type AuthRequiredListener = (event: AuthRequiredEvent) => void;
type AuthRequiredRecoveryListener = (
  event: AuthRequiredEvent,
) => boolean | Promise<boolean>;
type AuthLifecycleListener = (event: AuthLifecycleEvent) => void;

/** What a tab tells its siblings about the session, over BroadcastChannel and a storage event. */
const authBroadcastMessageSchema = z.object({
  createdAt: z.number(),
  id: z.string(),
  type: z.enum(['auth-renewed', 'auth-failed', 'auth-signed-out']),
});
type AuthBroadcastMessage = Readonly<z.infer<typeof authBroadcastMessageSchema>>;
type AuthBroadcastEventType = AuthBroadcastMessage['type'];

const AUTH_BROADCAST_CHANNEL = 'contract-analyzer:auth-session';
const AUTH_BROADCAST_STORAGE_KEY = 'contract-analyzer:auth-session-event';

const authRequiredListeners = new Set<AuthRequiredListener>();
const authRecoveryListeners = new Set<AuthRequiredRecoveryListener>();
const authRenewedListeners = new Set<AuthLifecycleListener>();
const authFailedListeners = new Set<AuthLifecycleListener>();
const authSignedOutListeners = new Set<AuthLifecycleListener>();
const sessionDeadlineListeners = new Set<(expiresAt: number) => void>();
const seenBroadcastIds = new Set<string>();
let sessionAuthBlocked = false;

export function isSessionAuthBlocked(): boolean {
  return sessionAuthBlocked;
}

export function blockSessionAuth(): void {
  sessionAuthBlocked = true;
}

/**
 * Every authenticated response reports its session's deadline, so one arriving proves the
 * session good. If a 401 that landed after a renewal had blocked session requests, they
 * resume here: nothing else would clear that block, and presence would stay paused.
 */
export function publishSessionDeadline(expiresAt: number): void {
  if (sessionAuthBlocked) {
    sessionAuthBlocked = false;
    notifyLifecycle(authRenewedListeners, { source: 'local' });
  }
  for (const listener of sessionDeadlineListeners) listener(expiresAt);
}

export function subscribeSessionDeadline(listener: (expiresAt: number) => void): () => void {
  sessionDeadlineListeners.add(listener);
  return () => { sessionDeadlineListeners.delete(listener); };
}

let broadcastChannel: BroadcastChannel | null = null;
let crossTabListenersInitialized = false;
let broadcastSequence = 0;

function notifyLifecycle(
  listeners: ReadonlySet<AuthLifecycleListener>,
  event: AuthLifecycleEvent,
): void {
  for (const listener of listeners) {
    listener(event);
  }
}

function notifyLifecycleByType(type: AuthBroadcastEventType): void {
  switch (type) {
    case 'auth-renewed':
      sessionAuthBlocked = false;
      notifyLifecycle(authRenewedListeners, { source: 'remote' });
      return;
    case 'auth-failed':
      notifyLifecycle(authFailedListeners, { source: 'remote' });
      return;
    case 'auth-signed-out':
      notifyLifecycle(authSignedOutListeners, { source: 'remote' });
      return;
  }
}

function rememberBroadcastId(id: string): boolean {
  if (seenBroadcastIds.has(id)) {
    return false;
  }

  seenBroadcastIds.add(id);
  if (seenBroadcastIds.size > 100) {
    seenBroadcastIds.clear();
    seenBroadcastIds.add(id);
  }
  return true;
}

function handleBroadcastMessage(message: AuthBroadcastMessage): void {
  if (rememberBroadcastId(message.id)) {
    notifyLifecycleByType(message.type);
  }
}

function readStoredBroadcast(serialized: string): AuthBroadcastMessage | null {
  try {
    const message = authBroadcastMessageSchema.safeParse(JSON.parse(serialized));
    return message.success ? message.data : null;
  } catch (error) {
    // Only JSON.parse throws here, and only a SyntaxError.
    console.warn('Ignoring malformed auth session broadcast.', error);
    return null;
  }
}

function handleStorageEvent(event: StorageEvent): void {
  if (event.key !== AUTH_BROADCAST_STORAGE_KEY || event.newValue === null) {
    return;
  }

  const message = readStoredBroadcast(event.newValue);
  if (message !== null) {
    handleBroadcastMessage(message);
  }
}

function ensureCrossTabListeners(): void {
  if (crossTabListenersInitialized || typeof window === 'undefined') {
    return;
  }

  crossTabListenersInitialized = true;
  window.addEventListener('storage', handleStorageEvent);

  if (typeof BroadcastChannel !== 'undefined') {
    broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    broadcastChannel.onmessage = (event: MessageEvent<unknown>) => {
      const message = authBroadcastMessageSchema.safeParse(event.data);
      if (message.success) {
        handleBroadcastMessage(message.data);
      }
    };
  }
}

function createBroadcastMessage(type: AuthBroadcastEventType): AuthBroadcastMessage {
  broadcastSequence += 1;
  return {
    createdAt: Date.now(),
    id: `${Date.now()}:${broadcastSequence}:${Math.random().toString(36).slice(2)}`,
    type,
  };
}

function broadcastLifecycle(type: AuthBroadcastEventType): void {
  if (typeof window === 'undefined') {
    return;
  }

  ensureCrossTabListeners();
  const message = createBroadcastMessage(type);
  rememberBroadcastId(message.id);

  if (broadcastChannel !== null) {
    broadcastChannel.postMessage(message);
  }

  try {
    window.localStorage.setItem(AUTH_BROADCAST_STORAGE_KEY, JSON.stringify(message));
    window.localStorage.removeItem(AUTH_BROADCAST_STORAGE_KEY);
  } catch (error) {
    console.warn('Auth session broadcast could not be persisted for sibling tabs.', error);
  }
}

export async function requestAuthRecovery(error: ApiClientError): Promise<boolean> {
  sessionAuthBlocked = true;
  const event: AuthRequiredEvent = { error };
  for (const listener of authRequiredListeners) {
    listener(event);
  }

  const results = await Promise.all(
    [...authRecoveryListeners].map(async (listener) => await listener(event)),
  );
  return results.some((result) => result);
}

export function subscribeAuthRequired(listener: AuthRequiredListener): () => void {
  authRequiredListeners.add(listener);
  return () => {
    authRequiredListeners.delete(listener);
  };
}

export function subscribeAuthRecovery(listener: AuthRequiredRecoveryListener): () => void {
  authRecoveryListeners.add(listener);
  return () => {
    authRecoveryListeners.delete(listener);
  };
}

export function publishAuthRenewed(): void {
  sessionAuthBlocked = false;
  notifyLifecycle(authRenewedListeners, { source: 'local' });
  broadcastLifecycle('auth-renewed');
}

export function subscribeAuthRenewed(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authRenewedListeners.add(listener);
  return () => {
    authRenewedListeners.delete(listener);
  };
}

export function publishAuthFailed(): void {
  notifyLifecycle(authFailedListeners, { source: 'local' });
  broadcastLifecycle('auth-failed');
}

export function subscribeAuthFailed(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authFailedListeners.add(listener);
  return () => {
    authFailedListeners.delete(listener);
  };
}

export function publishAuthSignedOut(): void {
  notifyLifecycle(authSignedOutListeners, { source: 'local' });
  broadcastLifecycle('auth-signed-out');
}

export function subscribeAuthSignedOut(listener: AuthLifecycleListener): () => void {
  ensureCrossTabListeners();
  authSignedOutListeners.add(listener);
  return () => {
    authSignedOutListeners.delete(listener);
  };
}
